import type { ListBuilderConfiguration } from '../types';

/**
 * Shared direction value mapping
 */
const DIRECTION_VALUE_MAPPING = [
	{ value: 'inbound', displayText: 'Inbound' },
	{ value: 'outbound', displayText: 'Outbound' },
];

/**
 * Shared internal/external value mapping
 */
const INTERNAL_VALUE_MAPPING = [
	{ value: '1', displayText: 'Internal' },
	{ value: '0', displayText: 'External' },
	{ value: 'true', displayText: 'Internal' },
	{ value: 'false', displayText: 'External' },
];

/**
 * Get standard call history columns
 */
const getCallHistoryColumns = () => [
	{
		id: 'call-date',
		headers: [{ id: 'call-date-header', text: 'Date' }],
		fields: [
			{
				id: 'date-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'date', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { dateFormat: 'datetime-medium' },
				},
				displayStyle: 'date' as const,
			},
		],
		sortable: true,
		width: 150,
		sortFields: [
			{
				direction: 'DESC' as const,
				fieldName: 'date',
				objectTable: 'call_user',
			},
		],
	},
	{
		id: 'user-name',
		headers: [{ id: 'user-name-header', text: 'User' }],
		fields: [
			{
				id: 'user-name-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'userName', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'text' as const,
			},
			{
				id: 'user-ext-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'phone-extension-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'phoneExtensionId', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: true,
		width: 150,
	},
	{
		id: 'external-phone',
		headers: [
			{ id: 'external-phone-header', text: 'Phone' },
			{ id: 'phone-line-break-header', text: '' },
			{ id: 'direction-header', text: 'Direction' },
			{ id: 'direction-line-break-header', text: '' },
			{ id: 'internal-header', text: 'Internal/External' },
		],
		fields: [
			{
				id: 'external-phone-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'externalPartyPhone', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'phone' as const,
			},
			{
				id: 'phone-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'direction-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'direction', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
					valueMapping: DIRECTION_VALUE_MAPPING,
				},
				displayStyle: 'text' as const,
			},
			{
				id: 'direction-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'internal-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'isInternal', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'xs' as const,
					colorRule: { type: 'theme' as const, themeColor: 'textSecondary' },
					valueMapping: INTERNAL_VALUE_MAPPING,
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 150,
	},
	{
		id: 'external-party',
		headers: [{ id: 'external-party-header', text: 'External Party' }],
		fields: [
			{
				id: 'matched-client-name-field',
				link: {
					type: 'object' as const,
					enabled: true,
					idField: 'clientId',
					objectTable: 'client',
				},
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'clientName', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'text' as const,
			},
			{
				id: 'client-external-party-line-break',
				dataConfig: { type: 'custom_text' as const, customText: '' },
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'line_break' as const,
			},
			{
				id: 'external-party-name-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'externalPartyName', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 180,
	},
	{
		id: 'duration',
		headers: [{ id: 'duration-header', text: 'Duration' }],
		fields: [
			{
				id: 'duration-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'duration', objectTable: 'call_user' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { customFormat: 'duration-seconds' },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: true,
		width: 100,
	},
	{
		id: 'notes',
		headers: [{ id: 'notes-header', text: 'Notes' }],
		fields: [
			{
				id: 'notes-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'notes', objectTable: 'call' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { truncate: 50 },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 200,
	},
	{
		id: 'ai-summary',
		headers: [{ id: 'ai-summary-header', text: 'AI Summary' }],
		fields: [
			{
				id: 'ai-summary-field',
				dataConfig: {
					type: 'field' as const,
					field: { fieldName: 'aiSummary', objectTable: 'call' },
				},
				styleConfig: {
					size: 'sm' as const,
					colorRule: { type: 'default' as const },
					formatOptions: { truncate: 50 },
				},
				displayStyle: 'text' as const,
			},
		],
		sortable: false,
		width: 200,
	},
];

/**
 * Call History list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CALL_HISTORY_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'todays-calls',
			cacheTTL: 300000,
			name: 'Today',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: getCallHistoryColumns(),
			defaultSortId: 'column-call-date-desc',
			sortFields: [
				{ objectTable: '_call_user', fieldName: 'date', direction: 'DESC' },
			],
			filterRules: {
				callHistoryUser: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
				},
				callHistoryDateRange: {
					defaultValue: [
						{
							id: 'today',
							label: 'Today',
							data: { startDate: '{{DATE:0d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
		{
			id: 'last-30-days-calls',
			cacheTTL: 300000,
			name: '30 Days',
			type: 'default',
			active: true,
			sortOrder: 1,
			columns: getCallHistoryColumns(),
			defaultSortId: 'column-call-date-desc',
			sortFields: [
				{ objectTable: '_call_user', fieldName: 'date', direction: 'DESC' },
			],
			filterRules: {
				callHistoryUser: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
				},
				callHistoryDateRange: {
					defaultValue: [
						{
							id: 'last-30-days',
							label: 'Last 30 Days',
							data: { startDate: '{{DATE:-30d}}', endDate: '{{DATE:0d}}' },
						},
					],
				},
			},
		},
		{
			id: 'all-calls',
			cacheTTL: 300000,
			name: 'All Dates',
			type: 'default',
			active: true,
			sortOrder: 2,
			columns: getCallHistoryColumns(),
			defaultSortId: 'column-call-date-desc',
			sortFields: [
				{ objectTable: '_call_user', fieldName: 'date', direction: 'DESC' },
			],
			filterRules: {
				callHistoryUser: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'call_history:view:any', negated: true },
						],
					},
				},
			},
		},
	],
	tableSorts: [
		{
			id: 'column-call-date-desc',
			name: 'Date (Newest First)',
			fields: [
				{ direction: 'DESC', fieldName: 'date', objectTable: 'call_user' },
			],
		},
		{
			id: 'column-call-date-asc',
			name: 'Date (Oldest First)',
			fields: [
				{ direction: 'ASC', fieldName: 'date', objectTable: 'call_user' },
			],
		},
		{
			id: 'duration-desc',
			name: 'Duration (High to Low)',
			fields: [
				{ direction: 'DESC', fieldName: 'duration', objectTable: 'call_user' },
			],
		},
		{
			id: 'duration-asc',
			name: 'Duration (Low to High)',
			fields: [
				{ direction: 'ASC', fieldName: 'duration', objectTable: 'call_user' },
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'callHistoryDateRange',
			'callHistoryUser',
			'callHistoryDirection',
			'callHistoryInternalExternal',
		],
	},
	showResetButton: true,
	objects: [
		{
			baseUrl: '/call-history/:id',
			objectLabel: 'Call',
			objectTable: 'call_user',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'date', fieldLabel: 'Date' },
				{ fieldName: 'userName', fieldLabel: 'User' },
				{ fieldName: 'externalPartyPhone', fieldLabel: 'External Phone' },
				{ fieldName: 'externalPartyName', fieldLabel: 'External Party' },
				{ fieldName: 'duration', fieldLabel: 'Duration' },
			],
		},
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
